import React, { JSX, useContext, useState, useEffect } from 'react';
import {
  Card,
  Table,
  Stack,
  TableRow,
  TableBody,
  TableCell,
  Container,
  TableContainer,
  Grid,
  IconButton,
  Typography,
  Button,
  Box,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import CircularProgress from '@mui/material/CircularProgress';
import { useNavigate } from 'react-router-dom';
import { Bounce } from 'react-toastify';
import { useQuery } from '@tanstack/react-query';
import { IoAddCircle } from 'react-icons/io5';
import moment from 'moment';

// Icons
import { IoClose } from 'react-icons/io5';
import { AiFillQuestionCircle } from 'react-icons/ai';
import { HiPlusCircle } from 'react-icons/hi2';

// Components
import Page from 'src/components/Page';
import Scrollbar from 'src/components/Scrollbar';
import SearchNotFound from 'src/components/SearchNotFound';
import { UserListHead } from 'src/sections/@dashboard/user';
import LineChart from 'src/components/LineChart';
import DonutChart from 'src/components/DonutChart';
import { TransactionModal } from 'src/components';

// Import new teacher transaction management components
import { TransactionStatusTracker, StudentEngagementReport } from 'src/components/teacher';

// Assets
import {
  LoginBg,
  VCurrencyIcon,
  CreditIcon,
  DebitIcon,
  DeleteIcon,
  EyeIcon,
  UserIcon,
  TransactionIcon,
} from 'src/images';

// Styles
import './../styles/dashboard.css';
import 'react-toastify/dist/ReactToastify.css';
import palette from 'src/theme/palette';

// Data
import { transactions, students } from 'src/utils/data';
import { useAuth } from 'src/context/auth/AuthContext';
import { useViewScope } from 'src/hooks/useViewScope';
import { studentService } from 'src/services/student.service';
import { studentBehaviourService } from 'src/services/student-behaviour.service';
import { cohortService } from 'src/services/cohort.service';
import { TeacherCohortSelector } from 'src/components/cohorts/TeacherCohortSelector';

// Types
interface TableHeadColumn {
  id: string;
  label: string;
  alignRight: boolean;
}

interface Student {
  id: string;
  studentName: string;
  email: string;
  phoneNumber?: string;
  grade?: string;
  balanceUSD: number;
}

interface CardData {
  title: string;
  amount: string;
  icon: string;
  color?: string;
}

interface VirtualCurrencyStats {
  totalBalance: number;
  totalCredit: number;
  totalDebit: number;
}

interface ToastOptions {
  position: 'top-right';
  autoClose: number;
  hideProgressBar: boolean;
  closeOnClick: boolean;
  pauseOnHover: boolean;
  draggable: boolean;
  progress: undefined;
  theme: 'light';
  transition: typeof Bounce;
}

const TABLE_HEAD: TableHeadColumn[] = [
  { id: 'name', label: 'Student Name', alignRight: false },
  { id: 'email', label: 'Email', alignRight: false },
  { id: 'studentId', label: 'Student ID', alignRight: false },
  { id: 'grade', label: 'Grade', alignRight: false },
  { id: 'balance', label: 'Balance $D', alignRight: false },
  // { id: 'behavior', label: 'Behavior Score', alignRight: false },
  { id: 'actions', label: 'Actions', alignRight: false },
];

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2),
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1),
  },
}));

function useTeacherDashboardData(user: any, selectedCohortId: string) {
  const { ownerFilterId: teacherUserId } = useViewScope('students');
  const cohortId = selectedCohortId !== 'ALL' ? selectedCohortId : undefined;

  const { data: platformStats, isLoading: isStatsLoading } = useQuery({
    queryKey: ['platform-financial-summary', user?.id, selectedCohortId],
    queryFn: () => studentService.getTotalVirtualCurrency({ teacherUserId, cohortId }),
  });

  const { data: behaviorStats, isLoading: isBehaviorLoading } = useQuery({
    queryKey: ['platform-behavior-summary', user?.id, selectedCohortId],
    queryFn: () => studentBehaviourService.getPlatformBehaviorCount({ teacherUserId, cohortId }),
  });

  const { data: monthlyBalances, isLoading: isBalancesLoading } = useQuery({
    queryKey: ['platform-monthly-balances', user?.id, selectedCohortId],
    queryFn: () => studentService.getPlatformMonthlyBalances({ teacherUserId, cohortId }),
  });

  const monthlyBalanceData =
    monthlyBalances?.map((item) => ({
      name: moment(item.month).format('MMM'),
      value: item.balance,
    })) || [];

  const { data: studentsData, isLoading: isTopStudentsLoading } = useQuery({
    queryKey: ['top-performing-students', user?.id, selectedCohortId],
    queryFn: () => studentService.getTopPerformingStudents({ limit: 10, teacherUserId, cohortId }),
  });

  const topStudentsData = React.useMemo(() => studentsData?.data || [], [studentsData]);
  const topStudentsCount = React.useMemo(() => studentsData?.count || 0, [studentsData]);

  return {
    platformStats,
    isStatsLoading,
    behaviorStats,
    isBehaviorLoading,
    monthlyBalanceData,
    isBalancesLoading,
    topStudentsData,
    topStudentsCount,
    isTopStudentsLoading,
  };
}

interface TopPerformingStudentsCardProps {
  isLoading: boolean;
  count: number;
  data: any[];
  onOpenTransactionModal: () => void;
  onViewStudent: (id: string) => void;
}

function TopPerformingStudentsCard({
  isLoading,
  count,
  data,
  onOpenTransactionModal,
  onViewStudent,
}: TopPerformingStudentsCardProps) {
  return (
    <Card style={{ borderRadius: 16, marginTop: 20 }}>
      <div className="headingRow table-header" style={{ padding: '10px 20px' }}>
        <Typography variant="h4">Top Performing Students</Typography>
        <Button className="_btn" style={{ width: 179 }} variant="contained" onClick={onOpenTransactionModal}>
          + New Transaction
        </Button>
      </div>

      <Scrollbar>
        <TableContainer sx={{ minWidth: 800 }}>
          <Table>
            <UserListHead headLabel={TABLE_HEAD} rowCount={count || 0} />

            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : count === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    <SearchNotFound searchQuery="No students found" />
                  </TableCell>
                </TableRow>
              ) : (
                data.map((student) => (
                  <TableRow hover key={student.id} tabIndex={-1} role="checkbox">
                    <TableCell
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                      }}
                      align="left"
                      onClick={() => onViewStudent(student.id)}
                    >
                      <IoAddCircle color={palette.primary.main} size={35} />
                      <Typography
                        variant="subtitle2"
                        noWrap
                        sx={{ ml: 2, '&:hover': { color: 'primary.main', textDecoration: 'underline' } }}
                      >
                        {student.firstname} {student.lastname}
                      </Typography>
                    </TableCell>

                    <TableCell align="left">
                      <Typography variant="subtitle2">{student.email}</Typography>
                    </TableCell>
                    <TableCell align="left">
                      <Typography variant="subtitle2">{student.grade}</Typography>
                    </TableCell>

                    <TableCell align="left">
                      <Typography variant="subtitle2">{student.grade}</Typography>
                    </TableCell>

                    <TableCell align="left">
                      <Typography
                        variant="subtitle2"
                        sx={{ color: student.balance > 0 ? 'text.primary' : 'error.main' }}
                      >
                        {student.balance.toLocaleString()}
                      </Typography>
                    </TableCell>

                    <TableCell align="left">
                      <Stack direction="row" spacing={1}>
                        <img
                          src={EyeIcon}
                          alt="View"
                          style={{ cursor: 'pointer' }}
                          onClick={() => onViewStudent(student.id)}
                        />
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Scrollbar>
    </Card>
  );
}

export default function Dashboard(): JSX.Element {
  const navigate = useNavigate();
  const [open, setOpen] = useState<boolean>(false);
  const [open1, setOpen1] = useState<boolean>(false);
  const { user, getBaseRoute, hasPermission } = useAuth();
  const [selectedCohortId, setSelectedCohortId] = useState<string>('ALL');

  const { data: teacherCohorts = [] } = useQuery({
    queryKey: ['teacher-cohorts', user?.id],
    queryFn: () => cohortService.getCohorts(),
  });

  const {
    platformStats,
    isStatsLoading,
    behaviorStats,
    isBehaviorLoading,
    monthlyBalanceData,
    isBalancesLoading,
    topStudentsData,
    topStudentsCount,
    isTopStudentsLoading,
  } = useTeacherDashboardData(user, selectedCohortId);

  const handleViewStudent = (id: string): void => {
    navigate(getBaseRoute(`/view-student/${id}`));
  };

  return (
    <Page title="Users">
      <Container maxWidth={false} sx={{ px: 1 }}>
        <div className="dashboardBanner">
          <div>
            <Typography color="white" variant="h2" className="_welcome_title">
              {/* @ts-ignore */}
              Welcome back, {user?.displayName}!
            </Typography>
            <p>Empowering Students, One Point at a Time!</p>
            <button className="_btn" onClick={() => setOpen(true)} style={{ width: 179 }}>
              + New Transaction
            </button>
          </div>
          <img src={LoginBg} alt="Dashboard Banner" />
        </div>

        {!hasPermission('students.view_all') && (
          <TeacherCohortSelector
            cohorts={teacherCohorts}
            selectedCohortId={selectedCohortId}
            onSelectCohort={setSelectedCohortId}
          />
        )}

        <Grid container spacing={2}>
          {[
            {
              title: 'Total Virtual Currency',
              amount: platformStats?.totalBalance.toLocaleString() ?? '0',
              icon: VCurrencyIcon,
              color: palette.primary.main,
            },
            {
              title: 'Total Credit Amount',
              amount: platformStats?.totalCredit.toLocaleString() ?? '0',
              icon: CreditIcon,
              color: palette.secondary.main,
            },
            {
              title: 'Total Debit Amount',
              amount: platformStats?.totalDebit.toLocaleString() ?? '0',
              icon: DebitIcon,
              color: palette.warning.main,
            },
          ].map((item, index) => (
            <Grid item sm={6} xs={12} md={4} lg={4} xl={4} key={index}>
              <div className="_payment_card">
                <div>
                  {isStatsLoading ? (
                    <CircularProgress size={24} />
                  ) : (
                    <>
                      <Typography variant="h3">{item.amount} $D</Typography>
                      <Typography variant="caption">{item.title}</Typography>
                    </>
                  )}
                </div>
                <div className="_card_icon_view" style={{ backgroundColor: item.color }}>
                  <img src={item.icon} alt={item.title} />
                </div>
              </div>
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={2} alignItems="center">
          <Grid item sm={6} xs={12} md={8} lg={8} xl={8}>
            <div className="_chart_view">
              {isBalancesLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <LineChart label="S.T.R.E.E.T CENTS" data={monthlyBalanceData} />
              )}
            </div>
          </Grid>
          <Grid item sm={6} xs={12} md={4} lg={4} xl={4}>
            <div className="_chart_view">
              {isBehaviorLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <DonutChart
                  label="S.T.R.E.E.T CRED"
                  data={[behaviorStats?.positive || 0, behaviorStats?.negative || 0]}
                  labels={['Highlights', 'Challenges']}
                  colors={[palette.primary.main, palette.error.main]}
                />
              )}
            </div>
          </Grid>
        </Grid>

        <TopPerformingStudentsCard
          isLoading={isTopStudentsLoading}
          count={topStudentsCount}
          data={topStudentsData}
          onOpenTransactionModal={() => setOpen(true)}
          onViewStudent={handleViewStudent}
        />

        {/* Transaction Management Section */}
        {/* <Grid container spacing={3} sx={{ mt: 2 }}>
          <Grid item xs={12}>
            <TransactionStatusTracker teacherId={user?.id || ''} />
          </Grid>
          <Grid item xs={12}>
            <StudentEngagementReport teacherId={user?.id || ''} />
          </Grid>
        </Grid> */}

        <TransactionModal open={open} closeModal={() => setOpen(false)} />

        <BootstrapDialog aria-labelledby="customized-dialog-title" open={open1} maxWidth="xs" fullWidth>
          <div className="_user_modal_header">
            <div />
            <span onClick={() => setOpen1(false)}>
              <IoClose size={25} color={palette.grey[500]} />
            </span>
          </div>

          <DialogContent>
            <div className="_user_modal_body" style={{ padding: '0px 20px' }}>
              <AiFillQuestionCircle size={100} color="#F63D58" />
              <Typography variant="h2" color={palette.text.primary} style={{ marginTop: 20 }}>
                Are You Sure?
              </Typography>
              <Typography variant="subtitle2" color={palette.grey[600]} style={{ fontSize: 16, marginTop: 10 }}>
                You want to delete the student?
              </Typography>
              <Button
                variant="contained"
                fullWidth
                style={{ height: 55, backgroundColor: '#F63D58', marginTop: 20 }}
                onClick={() => setOpen1(false)}
              >
                Yes, Delete
              </Button>
            </div>
          </DialogContent>
        </BootstrapDialog>

      </Container>
    </Page>
  );
}
